import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { formatINR, resolveImage, formatTimeToAMPM } from '../../../Utils/helper';
import { useAppSelector, useAppDispatch } from '../../../Redux/Hooks';
import { getBusinessDetailsAPI } from '../../../Redux/ApiCalls/Auth/user';
import { IMAGE_CDN } from '../../../config/config';
import { getSavedDefaultTerms } from './DocumentTermsPdfModal';

// ── Root Off-screen Container ────────────────────────────────────────────────
const PrintRoot = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
`;

const PrintDocument = styled.div`
  width: 794px;
  background: transparent;
  box-sizing: border-box;
  color: #111827;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;

  @media print {
    .document-page {
      page-break-after: always;
      break-after: page;
      margin-bottom: 0 !important;
      border: 1.5px solid #111827 !important;
    }
  }
`;

// ── Social Icons (Inline SVG) ────────────────────────────────────────────────
const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="12" fill="#1877F2" />
    <path
      d="M14.5 12.1h-1.8v6.9h-2.9v-6.9H8.5V9.7h1.3V8.1c0-1.1.5-2.8 2.8-2.8h2.1v2.4h-1.5c-.2 0-.6.1-.6.7v1.3h2.1l-.2 2.4z"
      fill="#ffffff"
    />
  </svg>
);

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <defs>
      <radialGradient id="printIgGrad" cx="30%" cy="105%" r="130%">
        <stop offset="0%" stopColor="#fdf497" />
        <stop offset="5%" stopColor="#fdf497" />
        <stop offset="45%" stopColor="#fd5949" />
        <stop offset="60%" stopColor="#d6249f" />
        <stop offset="90%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <rect width="24" height="24" rx="6" fill="url(#printIgGrad)" />
    <rect x="5.5" y="5.5" width="13" height="13" rx="3.5" stroke="#ffffff" strokeWidth="1.6" fill="none" />
    <circle cx="12" cy="12" r="3.2" stroke="#ffffff" strokeWidth="1.6" fill="none" />
    <circle cx="15.8" cy="8.2" r="0.9" fill="#ffffff" />
  </svg>
);

const YouTubeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', display: 'inline-block' }}>
    <path
      d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"
      fill="#FF0000"
    />
    <polygon points="9.8 15.2 15.8 12 9.8 8.8" fill="#ffffff" />
  </svg>
);

// ── Section Title with Dark Bar - Flex stretch (bar matches text height exactly) ───
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'stretch',
      margin: '8px 0 8px 0',
      
    }}
  >
    {/* Bar: stretches to the exact same height as the text sibling */}
    <div
      style={{
        width: '4px',
        backgroundColor: '#111827',
        flexShrink: 0,
      }}
    />
    {/* Title text */}
    <div
      style={{
        paddingLeft: '8px',
        paddingBottom: '14px',
        fontSize: '11px',
        fontWeight: 800,
        color: '#111827',
        letterSpacing: '0.8px',
        textTransform: 'uppercase',
        lineHeight: '1.4',
     
      }}
    >
      {title}
    </div>
  </div>
);


// ── Default 8-Point Terms & Conditions ───────────────────────────────────────
const DEFAULT_TERMS: Array<{ title: string; description: string }> = [
  {
    title: 'Payment Terms',
    description:
      '50% Advance (Non-Refundable) at the time of booking. 40% payable immediately after completion of the event. Remaining 10% to be cleared within 2 months from the date of the event.',
  },
  {
    title: 'Delivery of Videos & Albums',
    description: 'Final Video and/or Album will be delivered only after receipt of full and final payment.',
  },
  {
    title: 'Event Timing',
    description: 'We value punctuality. Kindly provide the exact event timings to ensure smooth service.',
  },
  {
    title: 'Soft Copies of Images',
    description: 'Soft copies will be released only after full and final payment has been received.',
  },
  {
    title: 'Responsibility for Loss/Damage',
    description:
      'The client shall be fully responsible for any loss or damage to our equipment or staff, caused by the client or their guests.',
  },
  {
    title: 'Data Responsibility',
    description:
      'Clients are requested to collect their data (videos, photos, albums, etc.) within 3 months after full and final payment. After this period, if data is deleted, corrupted, or lost, the studio shall not be held responsible.',
  },
  {
    title: 'Geographical Applicability',
    description: 'This offer is applicable only within Surat city limits.',
  },
  {
    title: 'Jurisdiction',
    description: 'All disputes, if any, shall be subject to Surat jurisdiction only.',
  },
];

interface DocumentPrintTemplateProps {
  printRef: React.RefObject<HTMLDivElement>;
  activeDoc: any;
  user: any;
  type: 'quotation' | 'order' | 'invoice';
  studioLogo?: string | null;
  studioName?: string | null;
  studioAddress?: string | null;
  addTerms?: boolean;
  terms?: string | null;
}

export const DocumentPrintTemplate: React.FC<DocumentPrintTemplateProps> = ({
  printRef,
  activeDoc,
  user,
  type,
  studioLogo,
  studioName,
  studioAddress,
  addTerms,
  terms,
}) => {
  const dispatch = useAppDispatch();
  const {
    studio_logo: authStudioLogo,
    business: authBusiness,
    user: authUser,
    SocialLinks: authSocialLinks,
  } = useAppSelector((state) => state.auth);

  const [localStudioLogo, setLocalStudioLogo] = useState<string | null>(null);

  // If business details haven't been loaded into redux yet, fetch them
  useEffect(() => {
    let isMounted = true;
    const fetchBusiness = async () => {
      try {
        const res: any = await dispatch(getBusinessDetailsAPI()).unwrap();
        const bd = res?.business_details || res?.business || res?.data?.business || res?.data?.business_details || res?.data || res;
        const logo = bd?.studio_logo || bd?.studio_logo_link || bd?.logo || bd?.image;
        if (isMounted && logo) {
          setLocalStudioLogo(logo);
        }
      } catch {
        // ignore
      }
    };
    if (!authStudioLogo && !authBusiness?.studio_logo) {
      fetchBusiness();
    }
    return () => {
      isMounted = false;
    };
  }, [authStudioLogo, authBusiness, dispatch]);

  const effectiveUser = user || authUser;

  const cachedUserInfo = useMemo(() => {
    try {
      const stored = localStorage.getItem('user_info');
      if (stored) return JSON.parse(stored);
    } catch {}
    return null;
  }, []);

  const effectiveStudioName =
    studioName ||
    activeDoc?.studio_name ||
    activeDoc?.business?.studio_name ||
    authBusiness?.studio_name ||
    cachedUserInfo?.studio_name ||
    cachedUserInfo?.business?.studio_name ||
    (effectiveUser as any)?.studio_name ||
    (effectiveUser as any)?.business?.studio_name ||
    'BOLLYWOOD STUDIO';

  // Comprehensive fallback search for studio logo across activeDoc, props, auth state, and localStorage
  const rawLogo =
    studioLogo ||
    localStudioLogo ||
    activeDoc?.studio_logo ||
    activeDoc?.studio_logo_link ||
    activeDoc?.business?.studio_logo ||
    activeDoc?.business?.studio_logo_link ||
    activeDoc?.business?.logo ||
    activeDoc?.business?.image ||
    activeDoc?.business_details?.studio_logo ||
    activeDoc?.business_details?.studio_logo_link ||
    activeDoc?.business_details?.logo ||
    activeDoc?.business_logo ||
    activeDoc?.logo ||
    activeDoc?.user?.studio_logo ||
    activeDoc?.user?.image ||
    activeDoc?.user?.profile_image ||
    authStudioLogo ||
    authBusiness?.studio_logo ||
    authBusiness?.studio_logo_link ||
    authBusiness?.logo ||
    authBusiness?.image ||
    cachedUserInfo?.studio_logo ||
    cachedUserInfo?.studio_logo_link ||
    cachedUserInfo?.business?.studio_logo ||
    cachedUserInfo?.business?.logo ||
    cachedUserInfo?.image ||
    cachedUserInfo?.profile_image ||
    (effectiveUser as any)?.studio_logo ||
    (effectiveUser as any)?.studio_logo_link ||
    (effectiveUser as any)?.business?.studio_logo ||
    (effectiveUser as any)?.business?.studio_logo_link ||
    (effectiveUser as any)?.business?.logo ||
    (effectiveUser as any)?.image ||
    (effectiveUser as any)?.profile_image ||
    (authUser as any)?.studio_logo ||
    (authUser as any)?.image ||
    (authUser as any)?.profile_image ||
    null;

  const rawLogoPath =
    typeof rawLogo === 'string'
      ? rawLogo
      : (rawLogo as any)?.url ||
        (rawLogo as any)?.image ||
        (rawLogo as any)?.link ||
        (rawLogo as any)?.file ||
        (rawLogo as any)?.path ||
        null;

  const resolvedLogoUrl = useMemo(() => {
    if (!rawLogoPath || typeof rawLogoPath !== 'string' || !rawLogoPath.trim()) return null;
    return (
      resolveImage(rawLogoPath) ||
      (rawLogoPath.startsWith('http://') || rawLogoPath.startsWith('https://')
        ? rawLogoPath
        : `${IMAGE_CDN}/${rawLogoPath.replace(/^\//, '')}`)
    );
  }, [rawLogoPath]);

  const [logoSrc, setLogoSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!resolvedLogoUrl) {
      setLogoSrc(null);
      return;
    }

    let isMounted = true;
    setLogoSrc(resolvedLogoUrl);

    // Convert to Base64 in memory to guarantee CORS-free capture in html2canvas
    const convert = async () => {
      try {
        const response = await fetch(resolvedLogoUrl);
        if (!response.ok) return;
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (isMounted && typeof reader.result === 'string') {
            setLogoSrc(reader.result);
          }
        };
        reader.readAsDataURL(blob);
      } catch {
        // Keep direct resolvedLogoUrl fallback
      }
    };
    convert();

    return () => {
      isMounted = false;
    };
  }, [resolvedLogoUrl]);

  // Social Links: Only display icons if studio owner added link in profile
  const fbLink =
    activeDoc?.business?.facebook_link ||
    activeDoc?.social_links?.facebook ||
    activeDoc?.socialLinks?.facebook ||
    authBusiness?.facebook_link ||
    authSocialLinks?.facebook_link ||
    null;

  const instaLink =
    activeDoc?.business?.insta_link ||
    activeDoc?.social_links?.instagram ||
    activeDoc?.socialLinks?.instagram ||
    authBusiness?.insta_link ||
    authSocialLinks?.insta_link ||
    null;

  const ytLink =
    activeDoc?.business?.youtube_link ||
    activeDoc?.social_links?.youtube ||
    activeDoc?.socialLinks?.youtube ||
    authBusiness?.youtube_link ||
    authSocialLinks?.youtube_link ||
    null;

  const showFb = Boolean(fbLink && typeof fbLink === 'string' && fbLink.trim());
  const showInsta = Boolean(instaLink && typeof instaLink === 'string' && instaLink.trim());
  const showYt = Boolean(ytLink && typeof ytLink === 'string' && ytLink.trim());

  const effectiveAddTerms =
    addTerms !== undefined
      ? addTerms
      : activeDoc?.add_terms !== undefined
      ? Boolean(activeDoc.add_terms)
      : activeDoc?.add_terms_conditions !== undefined
      ? Boolean(activeDoc.add_terms_conditions)
      : true;

  const savedTermsTitle = typeof window !== 'undefined' ? localStorage.getItem('studio_terms_title') : null;
  const termsTitle = savedTermsTitle?.trim() || "TERMS & CONDITIONS";

  const effectiveTerms =
    terms !== undefined
      ? terms
      : activeDoc?.terms || activeDoc?.terms_and_conditions || getSavedDefaultTerms();

  const hasPage2 = Boolean(
    effectiveAddTerms &&
    effectiveTerms &&
    typeof effectiveTerms === 'string' &&
    effectiveTerms.trim()
  );

  if (!activeDoc) return null;

  const isQuotation = type === 'quotation';
  const isInvoice = type === 'invoice';

  // ── Pricing Calculations ───────────────────────────────────────────────────
  let calculatedSubtotal = 0;

  if (isInvoice && activeDoc.line_items?.length > 0) {
    calculatedSubtotal += activeDoc.line_items.reduce(
      (sum: number, item: any) => sum + (Number(item.unit_price) || 0) * (Number(item.quantity) || 0),
      0
    );
  }
  if (activeDoc.resources?.length > 0) {
    calculatedSubtotal += activeDoc.resources.reduce(
      (sum: number, r: any) => sum + (Number(r.unit_price) || 0) * (Number(r.quantity) || 0),
      0
    );
  }
  if (activeDoc.fix_charge_items?.length > 0) {
    calculatedSubtotal += activeDoc.fix_charge_items.reduce(
      (sum: number, fc: any) => sum + (Number(fc.unit_price) || 0) * (Number(fc.quantity) || 0),
      0
    );
  }

  const subtotal = calculatedSubtotal || Number(activeDoc.subtotal) || Number(activeDoc.total_amount) || 0;
  const discountPercent = Number(activeDoc?.discount_percent) || 0;
  const discountAmount = subtotal * (discountPercent / 100);
  const taxableValueAfterDiscount = subtotal - discountAmount;
  const taxPercent = Number(activeDoc?.tax_percent) || 0;
  const taxAmount = taxableValueAfterDiscount * (taxPercent / 100);
  const grandTotal = Number(activeDoc?.grand_total) || taxableValueAfterDiscount + taxAmount;

  const docNo = isQuotation
    ? activeDoc.quotation_no
    : isInvoice
      ? activeDoc.invoice_no
      : activeDoc.order_no;

  const docDate = isQuotation
    ? activeDoc.quotation_date
    : isInvoice
      ? activeDoc.invoice_date
      : activeDoc.order_date;

  const docTypeLabel = isQuotation ? 'QUOTATION' : isInvoice ? 'TAX INVOICE' : 'ORDER';

  // Format Resources string for each event (e.g. "02×1P+1V")
  const getEventResourceString = (event: any) => {
    if (event.resource) return event.resource;
    if (event.resources_summary) return event.resources_summary;
    if (event.resources && typeof event.resources === 'string') return event.resources;

    if (activeDoc.resources && Array.isArray(activeDoc.resources) && event.id) {
      const linked = activeDoc.resources.filter((r: any) => r.event === event.id);
      if (linked.length > 0) {
        return linked.map((r: any) => `${String(r.quantity || 1).padStart(2, '0')}×${r.resource_name}`).join(', ');
      }
    }
    return '02×1P+1V';
  };

  // Product Details List (Numbered)
  const productDetailsList: string[] = useMemo(() => {
    if (activeDoc.product_info_description && typeof activeDoc.product_info_description === 'string') {
      const lines = activeDoc.product_info_description
        .split('\n')
        .map((l: string) => l.replace(/^\d+[\.\)\-]\s*/, '').trim())
        .filter(Boolean);
      if (lines.length > 0) return lines;
    }
    if (activeDoc.resources && Array.isArray(activeDoc.resources) && activeDoc.resources.length > 0) {
      return activeDoc.resources.map((r: any) =>
        r.quantity > 1 ? `${r.resource_name} - ${String(r.quantity).padStart(2, '0')}` : r.resource_name
      );
    }
    return [
      'Cinematic Highlight - 01',
      'Wedding Full Video',
      'Reels - 05',
      'Total Album :- 2 (250-250 Photos)',
      'Album (12x36 :- 2 Album)',
    ];
  }, [activeDoc]);

  // Album details normalization
  const albumRows = useMemo(() => {
    const list: any[] = [];
    if (Array.isArray(activeDoc.album_details) && activeDoc.album_details.length > 0) {
      list.push(...activeDoc.album_details);
    } else if (activeDoc.album_detail) {
      list.push(activeDoc.album_detail);
    }
    return list;
  }, [activeDoc]);

  // Additional Services normalization
  const additionalServicesRows = useMemo(() => {
    const rows: Array<{ service: string; details: string; amount: string }> = [];
    const optSource = activeDoc.optional_services || activeDoc.album_detail;
    if (optSource) {
      if (optSource.opt_album_page_cost || Number(optSource.opt_album_page_price) > 0) {
        rows.push({
          service: 'Extra Album Pages',
          details: optSource.opt_album_page_cost || '12x36 pages',
          amount: Number(optSource.opt_album_page_price) > 0 ? `₹${formatINR(optSource.opt_album_page_price)}` : '₹500.00',
        });
      }
      if (optSource.opt_video_cost || Number(optSource.opt_video_price) > 0) {
        rows.push({
          service: 'Extra Video Hours',
          details: optSource.opt_video_cost || 'Per Reel hours',
          amount: Number(optSource.opt_video_price) > 0 ? `₹${formatINR(optSource.opt_video_price)}` : '₹1500.00',
        });
      }
      if (Number(optSource.opt_pad_price_from) > 0 || Number(optSource.opt_pad_price_to) > 0) {
        rows.push({
          service: 'Album Padding',
          details: '-',
          amount: `₹${formatINR(optSource.opt_pad_price_from || 0)} - ₹${formatINR(optSource.opt_pad_price_to || 0)}`,
        });
      }
    }

    if (rows.length === 0) {
      rows.push(
        { service: 'Extra Album Pages', details: '12x36 pages', amount: '₹500.00' },
        { service: 'Extra Video Hours', details: 'Per Reel hours', amount: '₹1500.00' },
        { service: 'Album Padding', details: '-', amount: '₹2500.00 - ₹5000.00' }
      );
    }
    return rows;
  }, [activeDoc]);

  const tableBorderStyle: React.CSSProperties = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
  };

  const thStyle: React.CSSProperties = {
    border: '1px solid #e5e7eb',
    padding: '6px 10px',
    fontSize: '10px',
    fontWeight: 800,
    color: '#6b7280',
    textTransform: 'uppercase',
    textAlign: 'left',
    backgroundColor: '#ffffff',
  };

  const tdStyle: React.CSSProperties = {
    border: '1px solid #e5e7eb',
    padding: '6px 10px',
    fontSize: '11px',
    color: '#111827',
    verticalAlign: 'middle',
  };

  return (
    <PrintRoot>
      <PrintDocument ref={printRef}>
        {/* ══════════════════════════════════════════════════════════
            PAGE 1: COMMERCIAL & PRODUCT SPECIFICATIONS
        ══════════════════════════════════════════════════════════ */}
        <div
          className="document-page pdf-page"
          style={{
            width: '794px', // A4 width at 96 DPI
            minHeight: '1123px',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            padding: '16px 20px', // Space before page start and page end, left and right
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Inner frame with crisp, clearly visible page border */}
          <div
            style={{
              flex: 1,
              minHeight: '1091px',
              border: '1.5px solid #111827',
              borderRadius: '2px',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxSizing: 'border-box',
              backgroundColor: '#ffffff',
              color: '#111827',
              fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            <div>
              {/* ── 1. Top Header Box with Border ─────────────────────────── */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 24px',
                  borderBottom: '1.5px solid #111827', // Connects with outer border line, just bottom line is visible
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box',
                  width: '100%',
                }}
              >
                {/* Studio Logo */}
                <div style={{ flex: '0 0 auto', minWidth: '100px', display: 'flex', alignItems: 'center' }}>
                  {resolvedLogoUrl ? (
                    <img
                      src={logoSrc || resolvedLogoUrl}
                      alt={effectiveStudioName}
                      style={{ maxHeight: '60px', maxWidth: '140px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{ width: '40px' }} />
                  )}
                </div>

              {/* Studio Name & Tagline */}
              <div style={{ flex: 1, textAlign: 'center', padding: '0 12px' }}>
                <h1
                  style={{
                    margin: 0,
                    fontSize: '28px',
                    fontWeight: 800,
                    color: '#991b1b', // Bold Crimson/Maroon
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                  }}
                >
                  {effectiveStudioName}
                </h1>
                <p
                  style={{
                    margin: '3px 0 0 0',
                    fontSize: '13px',
                    fontStyle: 'italic',
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    color: '#374151',
                  }}
                >
                  One Stop Of Photography Services
                </p>
              </div>

              {/* Social Media Icons (Only show if added in profile) */}
              <div
                style={{
                  flex: '0 0 auto',
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  minWidth: '100px',
                  justifyContent: 'flex-end',
                }}
              >
                {showFb && <FacebookIcon />}
                {showInsta && <InstagramIcon />}
                {showYt && <YouTubeIcon />}
              </div>
            </div>

            {/* ── Page Content Container with Padding ────────────────── */}
            <div style={{ padding: '0 24px' }}>
              {/* ── 2. Meta Sub-header ────────────────────────────────────── */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px solid #e5e7eb',
                marginBottom: '14px',
              }}
            >
              {/* Quotation / Document Number */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {docTypeLabel}
                </div>
                <div style={{ fontSize: '17px', fontWeight: 800, color: '#374151' }}>
                  {docNo}
                </div>
              </div>

              {/* Date */}
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  DATE
                </div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#4b5563' }}>
                  {docDate}
                </div>
              </div>

              {/* Status & Studio Name */}
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#16a34a' }}>
                  {activeDoc.status || 'Active'}
                </div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                  {effectiveStudioName}
                </div>
              </div>
            </div>

            {/* ── 3. CUSTOMER INFORMATION ────────────────────────────── */}
            <SectionHeader title="CUSTOMER INFORMATION" />
            <table style={{ ...tableBorderStyle, marginBottom: '14px' }}>
              <tbody>
                <tr>
                  <td style={{ ...thStyle, width: '13%', backgroundColor: '#ffffff' }}>NAME</td>
                  <td style={{ ...tdStyle, width: '37%' }}>{activeDoc.customer_name || '—'}</td>
                  <td style={{ ...thStyle, width: '13%', backgroundColor: '#ffffff' }}>MOBILE</td>
                  <td style={{ ...tdStyle, width: '37%' }}>{activeDoc.customer_phone || '—'}</td>
                </tr>
                <tr>
                  <td style={{ ...thStyle, width: '13%', backgroundColor: '#ffffff' }}>EMAIL</td>
                  <td style={{ ...tdStyle, width: '37%' }}>{activeDoc.customer_email || '—'}</td>
                  <td style={{ ...thStyle, width: '13%', backgroundColor: '#ffffff' }}>ADDRESS</td>
                  <td style={{ ...tdStyle, width: '37%' }}>
                    {[activeDoc.customer_address, activeDoc.customer_place].filter(Boolean).join(', ') || '—'}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ── 4. FUNCTION DETAILS ────────────────────────────────── */}
            {activeDoc.events && activeDoc.events.length > 0 && (
              <>
                <SectionHeader title="FUNCTION DETAILS" />
                <table style={{ ...tableBorderStyle, marginBottom: '14px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: '18%' }}>DATE</th>
                      <th style={{ ...thStyle, width: '36%' }}>EVENT TYPE</th>
                      <th style={{ ...thStyle, width: '18%' }}>TIME</th>
                      <th style={{ ...thStyle, width: '28%' }}>RESOURCE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeDoc.events.map((e: any, idx: number) => {
                      const eventType = isQuotation ? (e.event_type_name || 'Event') : (e.event_type || 'Event');
                      const time = formatTimeToAMPM(e.event_time) || e.event_time || 'MORNING';
                      const resource = getEventResourceString(e);

                      return (
                        <tr key={idx}>
                          <td style={{ ...tdStyle, fontWeight: 700 }}>{e.event_date || '—'}</td>
                          <td style={tdStyle}>{eventType}</td>
                          <td style={{ ...tdStyle, textTransform: 'uppercase', fontSize: '10.5px', color: '#374151' }}>
                            {time}
                          </td>
                          <td style={tdStyle}>{resource}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}

            {/* ── 5. SPECIAL GIFTS ───────────────────────────────────── */}
            {activeDoc.gifts && activeDoc.gifts.length > 0 && (
              <>
                <SectionHeader title="SPECIAL GIFTS" />
                <table style={{ ...tableBorderStyle, marginBottom: '14px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: '70%' }}>PRODUCT</th>
                      <th style={{ ...thStyle, width: '30%' }}>QUANTITY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeDoc.gifts.map((g: any, idx: number) => (
                      <tr key={idx}>
                        <td style={tdStyle}>{g.gift_name}</td>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>
                          {String(g.quantity || 1).padStart(2, '0')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* ── 6. PRODUCT DETAILS ─────────────────────────────────── */}
            {productDetailsList.length > 0 && (
              <>
                <SectionHeader title="PRODUCT DETAILS" />
                <div style={{ marginBottom: '14px', paddingLeft: '4px' }}>
                  {productDetailsList.map((prod, idx) => (
                    <div key={idx} style={{ fontSize: '11px', color: '#111827', lineHeight: '1.7' }}>
                      <strong style={{ color: '#111827', marginRight: '6px' }}>{idx + 1}.</strong>
                      {prod}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── 7. ALBUM DETAILS ───────────────────────────────────── */}
            {albumRows.length > 0 && (
              <>
                <SectionHeader title="ALBUM DETAILS" />
                <table style={{ ...tableBorderStyle, marginBottom: '14px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: '32%' }}>ALBUM TYPE</th>
                      <th style={{ ...thStyle, width: '36%' }}>PAPER TYPE</th>
                      <th style={{ ...thStyle, width: '16%' }}>SIZE</th>
                      <th style={{ ...thStyle, width: '16%' }}>PAGES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {albumRows.map((alb: any, idx: number) => {
                      const size = alb.album_width && alb.album_height ? `${alb.album_width}X${alb.album_height}` : alb.size || '12X36';
                      return (
                        <tr key={idx}>
                          <td style={{ ...tdStyle, textTransform: 'uppercase' }}>{alb.album_type_name || alb.album_type || 'PHOTOBOOK-ALBUM'}</td>
                          <td style={tdStyle}>{alb.album_paper_name || alb.paper_type || 'NT Slim Matt Thermal'}</td>
                          <td style={tdStyle}>{size}</td>
                          <td style={tdStyle}>{alb.album_pages || alb.pages || '30'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}

            {/* ── 8. ADDITIONAL SERVICES ─────────────────────────────── */}
            {additionalServicesRows.length > 0 && (
              <>
                <SectionHeader title="ADDITIONAL SERVICES" />
                <table style={{ ...tableBorderStyle, marginBottom: '14px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: '40%' }}>SERVICE</th>
                      <th style={{ ...thStyle, width: '35%' }}>DETAILS</th>
                      <th style={{ ...thStyle, width: '25%', textAlign: 'right' }}>AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {additionalServicesRows.map((srv, idx) => (
                      <tr key={idx}>
                        <td style={tdStyle}>{srv.service}</td>
                        <td style={{ ...tdStyle, color: '#4b5563' }}>{srv.details}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{srv.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* ── 9. PRICING SUMMARY ─────────────────────────────────── */}
            <SectionHeader title="PRICING SUMMARY" />
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '16px' }}>
              {/* Notes Box (Left) */}
              <div
                style={{
                  flex: '1 1 54%',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '2px',
                  padding: '10px 14px',
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '4px' }}>
                  Notes
                </div>
                <div style={{ borderBottom: '1px dotted #d1d5db', marginBottom: '6px' }} />
                <div style={{ fontSize: '10px', color: '#4b5563', lineHeight: '1.45' }}>
                  Please ensure that all dues are cleared on time to avoid delays in delivery. The client is responsible for collecting all deliverables (albums, videos, and data) within 3 months after full and final payment. After this period, the studio will not be liable for any data loss, corruption, or unavailability.
                </div>
              </div>

              {/* Calculations Table (Right) */}
              <div style={{ flex: '1 1 46%' }}>
                <table style={{ ...tableBorderStyle, marginBottom: '6px' }}>
                  <tbody>
                    <tr>
                      <td style={{ ...tdStyle, fontWeight: 700, color: '#374151' }}>Total Amount:</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>₹{formatINR(subtotal)}</td>
                    </tr>
                    <tr>
                      <td style={{ ...tdStyle, color: '#4b5563' }}>Discount ({discountPercent.toFixed(2)}%):</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#16a34a' }}>- ₹{formatINR(discountAmount)}</td>
                    </tr>
                    <tr>
                      <td style={{ ...tdStyle, fontWeight: 700, color: '#374151' }}>Subtotal:</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>₹{formatINR(taxableValueAfterDiscount)}</td>
                    </tr>
                    <tr>
                      <td style={{ ...tdStyle, color: '#4b5563' }}>Tax ({taxPercent.toFixed(2)}%):</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>₹{formatINR(taxAmount)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Final Total Bar */}
                <div
                  style={{
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    padding: '6px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: '11px', fontWeight: 800, color: '#374151' }}>Final Total:</span>
                  <span style={{ fontSize: '14px', fontWeight: 800, color: '#111827' }}>₹{formatINR(grandTotal)}</span>
                </div>
              </div>
            </div>

            {/* Signatures Block on Page 1 if no Page 2 */}
            {!hasPage2 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '36px',
                  marginBottom: '12px',
                  padding: '0 16px',
                }}
              >
                <div style={{ textAlign: 'center', width: '200px' }}>
                  <div style={{ borderBottom: '1px solid #9ca3af', height: '36px', marginBottom: '6px' }} />
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151' }}>Customer Signature</div>
                </div>
                <div style={{ textAlign: 'center', width: '200px' }}>
                  <div style={{ borderBottom: '1px solid #9ca3af', height: '36px', marginBottom: '6px' }} />
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151' }}>Authorized Signatory</div>
                  <div style={{ fontSize: '10px', color: '#6b7280' }}>{effectiveStudioName}</div>
                </div>
              </div>
            )}
            </div>
          </div>

          {/* ── Page 1 Footer (Leaves Space Before Page End) ─────────── */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 24px 12px 24px',
              borderTop: '1px solid #f3f4f6',
              fontSize: '11px',
              color: '#6b7280',
            }}
          >
            <div>{docTypeLabel}: {docNo}</div>
            <div style={{ fontWeight: 600, color: '#374151' }}>Page 1 of {hasPage2 ? 2 : 1}</div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          PAGE 2: TERMS & CONDITIONS & SIGNATURES
      ══════════════════════════════════════════════════════════ */}
      {hasPage2 && (
        <div
          className="document-page pdf-page"
          style={{
          width: '794px', // A4 width at 96 DPI
          minHeight: '1123px',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          padding: '16px 20px', // Space before page start and page end, left and right
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Inner frame with crisp, clearly visible page border */}
        <div
          style={{
            flex: 1,
            minHeight: '1091px',
            border: '1.5px solid #111827',
            borderRadius: '2px',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
            backgroundColor: '#ffffff',
            color: '#111827',
            fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          }}
        >
          <div>
            {/* Top Banner on Page 2 */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 24px',
                borderBottom: '1.5px solid #111827',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
              }}
            >
              <div style={{ fontSize: '15px', fontWeight: 800, color: '#991b1b', fontFamily: "Georgia, 'Times New Roman', serif" }}>
                {effectiveStudioName}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>
                {docTypeLabel}: {docNo}
              </div>
            </div>

            <div style={{ padding: '0 24px' }}>
              {/* Terms & Conditions Section */}
              {effectiveAddTerms && (
                <>
                  <SectionHeader title={termsTitle} />
                  <div style={{ paddingLeft: '4px', marginTop: '12px' }}>
                    {effectiveTerms && typeof effectiveTerms === 'string' && effectiveTerms.trim() ? (
                      <div
                        style={{
                          fontSize: '11px',
                          lineHeight: '1.6',
                          color: '#374151',
                          whiteSpace: 'pre-wrap',
                          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        }}
                      >
                        {effectiveTerms}
                      </div>
                    ) : (
                      DEFAULT_TERMS.map((term, idx) => (
                        <div key={idx} style={{ marginBottom: '14px', fontSize: '11px', lineHeight: '1.5' }}>
                          <div style={{ fontWeight: 700, color: '#111827' }}>
                            {idx + 1}. {term.title}:
                          </div>
                          <div style={{ color: '#4b5563', marginTop: '2px' }}>{term.description}</div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {/* Signatures Block */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '60px',
                  padding: '0 20px',
                }}
              >
                <div style={{ textAlign: 'center', width: '200px' }}>
                  <div style={{ borderBottom: '1px solid #9ca3af', height: '40px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151' }}>Customer Signature</div>
                </div>
                <div style={{ textAlign: 'center', width: '200px' }}>
                  <div style={{ borderBottom: '1px solid #9ca3af', height: '40px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151' }}>Authorized Signatory</div>
                  <div style={{ fontSize: '10px', color: '#6b7280' }}>{effectiveStudioName}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Page 2 Footer (Leaves Space Before Page End) ───────── */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 24px 12px 24px',
              borderTop: '1px solid #f3f4f6',
              fontSize: '11px',
              color: '#6b7280',
            }}
          >
            <div>{docTypeLabel}: {docNo}</div>
            <div style={{ fontWeight: 600, color: '#374151' }}>Page 2 of 2</div>
          </div>
        </div>
      </div>
    )}
    </PrintDocument>
  </PrintRoot>
);
};

export default DocumentPrintTemplate;
